import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setReportEmpUnderLabourList } from "../../slices/reportSlice";
import * as services from "../../services/reportApi";
import { generateRandomString } from "utils/helper";

function useEmployeeUnderLabour() {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const { currentEmployee } = useSelector((state) => state.company)
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [showReport, setShowReport] = useState(false);
    const [search, setSearch] = useState({});
    const [isDownloading, setIsDownloading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const reportEmpUnderLabour = useSelector((state) => state.report.reportEmpUnderLabour || []);
    const isLoading = useSelector((state) => state.report.isLoading);

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n?.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions;
    }, [departmentListOption, i18n]);

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions?.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n?.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions;
    }, [i18n, positionOptions]);

    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
            });
            dispatch(setDepartmentListOption(response.data));
        } catch (error) {
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, dispatch]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                };
                const response = await services.getPosition(values);
                setPositionOptions(response.data);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, dispatch]);

    useEffect(() => {
        if (currentCompany) {
            getDepartment();
            getPosition();
        }
    }, [currentCompany, getDepartment, getPosition]);

    const getEmployeeUnderLabourTable = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));

                const response = await services.getEmployeeUnderLabourTableReport({
                    ...params,
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    ...params.pagination,
                });
                console.log("Response data jaaa", response?.data?.page?.content);

                const employeeNos = response?.data?.page?.content?.map((item) => item?.employeeInfo?.employeeNo);
                console.log("Employee Nos:", employeeNos);

                dispatch(setReportEmpUnderLabourList(response?.data?.page?.content));
                setPagination({
                    ...pagination,
                    // ...params?.request,
                    current: response?.data?.page?.pageable?.pageNumber + 1,
                    pageNo: response?.data?.page?.pageable?.pageNumber + 1,
                    size: response?.data?.page?.pageable?.pageSize,
                    total: response.data?.page?.totalElements,
                });
                return employeeNos;
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [dispatch, currentCompany, currentEmployee, pagination, reportEmpUnderLabour]
    );

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            pagination: {
                page: pagination?.page,
                size: pagination?.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;
    }

    const onChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {
            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            setPagination({ ...pagination, ...modifiedParams?.pagination })
            getEmployeeUnderLabourTable({ ...modifiedParams, request: pagination.request, ...search });
        },
        [getEmployeeUnderLabourTable, pagination]
    );

    const exportEmployeeUnderLabourReport = useCallback(
        async (params = {}) => {
            setIsDownloading(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportEmployeeUnderLabourReport({
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    ...params,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y",
                    sortBy: params.sortBy,
                    startDate: search?.month?.startOf('month').format('YYYY-MM-DD'),
                    endDate: search?.month?.endOf('month').format('YYYY-MM-DD')
                })
                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "ทะเบียนลูกจ้างตามพรบ_คุ้มครองแรงงาน.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
                setIsDownloading(false);
            }
        },
        [currentCompany, dispatch, pagination, search]
    );

    const handleSearch = (values) => {
        setShowReport(true);
        setSearch({})
        try {
            setSearch({
                ...values,
                startDate: values?.month ? values?.month?.startOf('month').format('YYYY-MM-DD') : null,
                endDate: values?.month ? values?.month?.endOf('month').format('YYYY-MM-DD') : null
            })
            getEmployeeUnderLabourTable({
                ...values,
                startDate: values?.month ? values?.month?.startOf('month').format('YYYY-MM-DD') : null,
                endDate: values?.month ? values?.month?.endOf('month').format('YYYY-MM-DD') : null,
                ...pagination
            })
        } catch (error) {
            console.log("error", error);
        }
    };

    const showModal = () => {
        setIsModalOpen(true);
    };

    return {
        departmentOptions: modifiedDepartmentOption,
        positionOptions: modifiedPositionOptions,
        showReport,
        handleSearch,
        isDownloading,
        isModalOpen,
        setIsModalOpen,
        showModal,
        reportEmpUnderLabour,
        pagination,
        onChange,
        isLoading,
        exportEmployeeUnderLabourReport
    }
}

export default useEmployeeUnderLabour;